import type { TSocialOfferSpotlight } from "@api";
import type { ISocialOffer } from "@bbj-types/offer";
import { SocialApplyStatus } from "@bbj-types/socialApply/enum";
import { useGlobalContext } from "@root/context";
import { isInternalOfferSource } from "@root/utils/enums/offer";
import { isAuthorize, useAuthorizer } from "@styleguide";
import { useMemo } from "react";

export const useDisplayFup = (
  offer: Pick<ISocialOffer, "source" | "provider"> & {
    status?: SocialApplyStatus;
  }
) => {
  const { user } = useGlobalContext();
  const isPartnerAuthorized = useAuthorizer(["partner.dashboard.visualize"]);
  const isOperatorAuthorized = isAuthorize(user.roleV2, ["offers.social.prescription.operator"]);

  return useMemo(() => {
    if (user.organisation.fup?.onlyManual && !isInternalOfferSource(offer.source)) {
      return false;
    }

    const validStatus =
      !offer.status ||
      offer.status === SocialApplyStatus.PROPOSED ||
      offer.status === SocialApplyStatus.INTERESTED_CANDIDATE;

    if (isPartnerAuthorized) {
      return validStatus && Boolean(offer.provider?.enableFup);
    }

    return validStatus && isOperatorAuthorized;
  }, [
    user.organisation.fup?.onlyManual,
    offer.source,
    offer.status,
    offer.provider?.enableFup,
    isPartnerAuthorized,
    isOperatorAuthorized,
  ]);
};

export const useDisplayFupSimple = (offer: Pick<ISocialOffer, "provider"> | TSocialOfferSpotlight) => {
  const isPartnerAuthorized = useAuthorizer(["partner.dashboard.visualize"]);
  const isFupAuthorized = useAuthorizer(["candidate.socialboard.apply.modify"]);

  return useMemo(() => {
    if (isPartnerAuthorized) {
      return Boolean(offer.provider?.enableFup);
    }
    return isFupAuthorized;
  }, [offer.provider?.enableFup, isPartnerAuthorized, isFupAuthorized]);
};
